<script setup lang="ts">
/**
 * AdditionsTabBar — Tab bar for switching between Drones, Fighters, Cargo, Implants
 * Displayed below slot groups in the fitting editor.
 */
import { ref, computed } from 'vue';

export type AdditionsTab = 'drones' | 'fighters' | 'cargo' | 'implants';

const props = defineProps<{
  isCarrier: boolean;
  droneCount: number;
  fighterCount: number;
  cargoCount: number;
  implantCount: number;
}>();

const emit = defineEmits<{
  'update:tab': [tab: AdditionsTab];
}>();

const activeTab = ref<AdditionsTab>('drones');

const tabs = computed(() => {
  const t: { id: AdditionsTab; label: string; count: number }[] = [
    { id: 'drones', label: 'Drones', count: props.droneCount },
    { id: 'cargo', label: 'Cargo', count: props.cargoCount },
    { id: 'implants', label: 'Implants', count: props.implantCount },
  ];
  if (props.isCarrier) {
    t.splice(1, 0, { id: 'fighters', label: 'Fighters', count: props.fighterCount });
  }
  return t;
});

function selectTab(tab: AdditionsTab): void {
  activeTab.value = tab;
  emit('update:tab', tab);
}

defineExpose({ activeTab });
</script>

<template>
  <div class="additions-tab-bar">
    <button
      v-for="tab in tabs"
      :key="tab.id"
      class="additions-tab"
      :class="{ active: activeTab === tab.id }"
      @click="selectTab(tab.id)"
    >
      {{ tab.label }}
      <span v-if="tab.count > 0" class="tab-count">{{ tab.count }}</span>
    </button>
  </div>
</template>

<style scoped>
.additions-tab-bar {
  display: flex;
  gap: 1px;
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  background: var(--border-color);
  flex-shrink: 0;
}

.additions-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-panel);
  border: none;
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.additions-tab:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.additions-tab.active {
  background: var(--bg-secondary);
  color: var(--accent-blue);
}

.tab-count {
  font-size: 0.65rem;
  background: rgba(74, 158, 255, 0.15);
  color: var(--accent-blue);
  padding: 0 5px;
  border-radius: 8px;
  line-height: 1.4;
}
</style>
